import React, { useEffect, useRef, useState } from 'react'
import Select, { components } from 'react-select'
import { Film, Paintbrush, Palette } from 'lucide-react'
import { themedSettingsSelectStyles } from 'components/shared/reactSelectStyles'
import { POST_MEDIA_ACTION_TOOLTIP } from 'components/answers/postMediaActions'
import { AiImageButtonIconSvg } from 'components/quests/AiMagicWandMenuButton'
import { isAndroid, isIOS } from 'lib/device'
import styles from './new-answer.module.scss'

const POST_MEDIA_ACTION_OPTIONS = [
    { value: 'illustrate', label: 'Illustrate', Icon: Paintbrush },
    { value: 'film', label: 'Film', Icon: Film },
]

// The AI models menu (the magic wand) lives in this dropdown as its last row,
// so the composer's toolbar carries one creative control instead of two.
export const AI_MODELS_MENU_OPTION_VALUE = 'models'
const AI_MODELS_MENU_OPTION = {
    value: AI_MODELS_MENU_OPTION_VALUE,
    label: 'Models',
    Icon: AiImageButtonIconSvg,
}

// On a phone the editor keeps focus while this menu opens and while a row is
// chosen, so the keyboard stays up under the menu. The menu is driven by hand
// here: react-select would focus its own input, which takes the keyboard down
// (on iOS the pill then folded and took the menu, or the Models dialog, with it).
const preserveEditorFocus = isAndroid || isIOS

const questionColor = 'var(--color-brand-question)'
const inverseTextColor = 'var(--color-text-inverse)'

const postMediaActionSelectStyles = {
    ...themedSettingsSelectStyles,
    container: base => ({
        ...base,
        height: 30,
        width: 30,
    }),
    control: (base, state) => {
        const menuOpen = state.selectProps.menuIsOpen
        return {
            ...themedSettingsSelectStyles.control(base, state),
            backgroundColor: menuOpen ? questionColor : 'transparent',
            border: 0,
            borderRadius: '50%',
            boxShadow: state.isFocused ? '0 0 0 2px var(--color-accent-light-purple)' : 'none',
            color: menuOpen ? inverseTextColor : questionColor,
            cursor: state.isDisabled ? 'default' : 'pointer',
            height: 30,
            minHeight: 30,
            opacity: state.isDisabled ? 0.4 : 1,
            width: 30,
            ':hover': {
                backgroundColor: state.isDisabled ? 'transparent' : questionColor,
                color: state.isDisabled ? questionColor : inverseTextColor,
            },
        }
    },
    valueContainer: base => ({
        ...base,
        alignItems: 'center',
        display: 'flex',
        height: 30,
        justifyContent: 'center',
        overflow: 'visible',
        padding: 0,
    }),
    placeholder: base => ({
        ...base,
        alignItems: 'center',
        color: 'currentColor',
        display: 'flex',
        margin: 0,
        position: 'static',
        transform: 'none',
    }),
    input: base => ({
        ...base,
        margin: 0,
        padding: 0,
    }),
    menu: base => ({
        ...themedSettingsSelectStyles.menu(base),
        width: 150,
    }),
    option: (base, state) => ({
        ...themedSettingsSelectStyles.option(base, state),
        cursor: 'pointer',
    }),
}

const PostMediaControl = props => {
    const originalMouseDown = props.innerProps.onMouseDown
    const originalTouchEnd = props.innerProps.onTouchEnd

    const activatePreservedControl = event => {
        event.preventDefault()
        event.stopPropagation()
        props.selectProps.onPreservedControlActivation?.()
    }

    return (
        <components.Control
            {...props}
            innerProps={{
                ...props.innerProps,
                'data-testid': props.selectProps.triggerTestId,
                onMouseDown: event => {
                    if (props.selectProps.preserveEditorFocusOnActivation) {
                        activatePreservedControl(event)
                        return
                    }

                    originalMouseDown?.(event)
                },
                onTouchEnd: event => {
                    if (props.selectProps.preserveEditorFocusOnActivation) {
                        activatePreservedControl(event)
                        return
                    }

                    originalTouchEnd?.(event)
                },
            }}
        />
    )
}

const PostMediaOption = props => {
    const originalMouseDown = props.innerProps.onMouseDown
    const originalTouchEnd = props.innerProps.onTouchEnd

    return (
        <components.Option
            {...props}
            innerProps={{
                ...props.innerProps,
                'data-testid': `${props.selectProps.menuTestId}-${props.data.value}`,
                onMouseDown: event => {
                    if (props.selectProps.preserveEditorFocusOnActivation) {
                        event.preventDefault()
                    }
                    originalMouseDown?.(event)
                },
                onTouchEnd: event => {
                    if (props.selectProps.preserveEditorFocusOnActivation) {
                        event.preventDefault()
                        event.stopPropagation()
                        props.innerProps.onClick?.(event)
                        return
                    }

                    originalTouchEnd?.(event)
                },
            }}
        />
    )
}

const PostMediaMenu = props => (
    <components.Menu
        {...props}
        innerProps={{
            ...props.innerProps,
            'data-testid': props.selectProps.menuTestId,
        }}
    />
)

const formatPostMediaActionOption = option => {
    const { Icon } = option
    return (
        <span className={styles.postMediaActionOption}>
            <Icon aria-hidden="true" />
            <span>{option.label}</span>
        </span>
    )
}

export default function PostMediaActionMenuButton({
    // Illustrate and Film work without text: an empty post gets a Treechat
    // house piece crafted from the member's recent activity.
    menuTestId,
    onMenuStateChange,
    onOpenModels,
    onSelect,
    triggerTestId,
}) {
    const menuPortalTarget = typeof document === 'undefined' ? undefined : document.body
    const containerRef = useRef(null)
    const [touchMenuOpen, setTouchMenuOpen] = useState(false)
    const options = onOpenModels
        ? [...POST_MEDIA_ACTION_OPTIONS, AI_MODELS_MENU_OPTION]
        : POST_MEDIA_ACTION_OPTIONS

    const setTouchMenuState = nextOpen => {
        setTouchMenuOpen(nextOpen)
        onMenuStateChange?.(nextOpen)
    }

    useEffect(() => {
        if (!preserveEditorFocus || !touchMenuOpen) return

        const closeOnOutsidePress = event => {
            const target = event.target
            if (!(target instanceof Element)) return
            if (containerRef.current?.contains(target)) return
            if (target.closest(`[data-testid="${menuTestId}"]`)) return
            setTouchMenuState(false)
        }

        document.addEventListener('mousedown', closeOnOutsidePress)
        document.addEventListener('touchstart', closeOnOutsidePress)
        return () => {
            document.removeEventListener('mousedown', closeOnOutsidePress)
            document.removeEventListener('touchstart', closeOnOutsidePress)
        }
    }, [touchMenuOpen, menuTestId, onMenuStateChange])

    return (
        <div ref={containerRef}>
            <Select
                aria-label={POST_MEDIA_ACTION_TOOLTIP}
                components={{
                    Control: PostMediaControl,
                    DropdownIndicator: null,
                    IndicatorSeparator: null,
                    Menu: PostMediaMenu,
                    Option: PostMediaOption,
                }}
                controlShouldRenderValue={false}
                formatOptionLabel={formatPostMediaActionOption}
                inputId={`${triggerTestId}-input`}
                instanceId={triggerTestId}
                isClearable={false}
                isSearchable={false}
                menuIsOpen={preserveEditorFocus ? touchMenuOpen : undefined}
                // On a phone the composer sits on the keyboard, and react-select's
                // 'auto' measures the layout viewport, which the keyboard does not
                // shrink, so it would open the menu down behind the keyboard.
                menuPlacement={preserveEditorFocus ? 'top' : 'auto'}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                menuTestId={menuTestId}
                onChange={option => {
                    if (option?.value === AI_MODELS_MENU_OPTION_VALUE) onOpenModels?.()
                    else if (option) onSelect(option.value)
                    if (preserveEditorFocus) setTouchMenuState(false)
                }}
                onMenuClose={() => {
                    if (preserveEditorFocus) setTouchMenuState(false)
                    else onMenuStateChange?.(false)
                }}
                onMenuOpen={() => {
                    if (!preserveEditorFocus) onMenuStateChange?.(true)
                }}
                onPreservedControlActivation={() => setTouchMenuState(!touchMenuOpen)}
                options={options}
                placeholder={<Palette aria-hidden="true" size={20} />}
                preserveEditorFocusOnActivation={preserveEditorFocus}
                styles={postMediaActionSelectStyles}
                triggerTestId={triggerTestId}
                value={null}
            />
        </div>
    )
}
